import type {
    ContextMenuCheckboxItemProps,
    ContextMenuItemProps,
    ContextMenuProps,
    ContextMenuRadioGroupProps,
    ContextMenuRadioItemProps,
    ContextMenuSubContentProps,
    ContextMenuSubProps,
    ContextMenuSubTriggerProps,
} from '@radix-ui/react-context-menu';
import {
    CheckboxItem,
    Content,
    Group,
    Item,
    ItemIndicator,
    Label,
    Portal,
    RadioGroup,
    RadioItem,
    Root,
    Separator,
    Sub,
    SubContent,
    SubTrigger,
    Trigger,
} from '@radix-ui/react-context-menu';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';

import { Tooltip } from '../tooltip/Tooltip';

// biome-ignore lint/complexity/noBannedTypes: suppressed due to migration
export type ContextMenuSeparatorProps = {};

export type ContextMenuLabelProps = {
    readonly children: ReactNode;
};

export type ContextMenuGroupProps = {
    readonly children: ReactNode;
};

const contentClasses = clsx(
    'bg-gray-100 border border-gray-300 rounded-xl shadow-md font-sans',
    'min-w-[220px] overflow-hidden p-xs z-[1000]',
);

const baseItemClasses = clsx(
    'flex items-center rounded-sm text-gray-900 cursor-pointer',
    'text-xxs font-normal leading-[1rem]',
    'py-s px-m relative select-none',
    'data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
);

const highlightedClasses =
    'data-[highlighted]:bg-gray-200 data-[highlighted]:text-gray-900 data-[highlighted]:outline-none';

const checkedClasses = 'data-[state=checked]:bg-gray-200';

const disabledGroupClasses =
    'data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none';

export const ContextMenu: FunctionComponent<ContextMenuProps> = ({ children, onOpenChange, ...props }) => {
    return (
        <Root onOpenChange={onOpenChange} {...props}>
            {children}
        </Root>
    );
};

export const ContextMenuTrigger: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return <Trigger asChild={true}>{children}</Trigger>;
};

export const ContextMenuItem: FunctionComponent<ContextMenuItemProps & { readonly tooltipContent?: string }> = ({
    children,
    className,
    disabled,
    onSelect,
    tooltipContent,
    ...props
}) => {
    const menuItem = (
        <Item
            className={clsx(
                baseItemClasses,
                'gap-xs',
                '[&_svg]:text-gray-600',
                highlightedClasses,
                'data-[disabled]:[&_svg]:text-gray-400',
                className,
            )}
            disabled={disabled}
            onSelect={onSelect}
            {...props}
        >
            {children}
        </Item>
    );

    if (disabled === true && is.nonEmptyString(tooltipContent)) {
        return (
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <div style={{ cursor: 'not-allowed', display: 'flex' }}>{menuItem}</div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="right" sideOffset={5}>
                        {tooltipContent}
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        );
    }

    return menuItem;
};

export const ContextMenuCheckboxItem: FunctionComponent<ContextMenuCheckboxItemProps> = ({
    checked,
    children,
    className,
    disabled,
    onCheckedChange,
    ...props
}) => {
    return (
        <CheckboxItem
            checked={checked}
            className={clsx(baseItemClasses, 'hover:bg-gray-200', highlightedClasses, checkedClasses, className)}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
            {...props}
        >
            {children}
        </CheckboxItem>
    );
};

export const ContextMenuSeparator: FunctionComponent<ContextMenuSeparatorProps> = ({ ...props }) => {
    return <Separator className="bg-gray-300 h-px m-xs" {...props} />;
};

export const ContextMenuLabel: FunctionComponent<ContextMenuLabelProps> = ({ children, ...props }) => {
    return (
        <Label className="text-gray-700 text-xxs font-normal p-s" {...props}>
            {children}
        </Label>
    );
};

export const ContextMenuGroup: FunctionComponent<ContextMenuGroupProps> = ({ children, ...props }) => {
    return (
        <Group className={disabledGroupClasses} {...props}>
            {children}
        </Group>
    );
};

export const ContextMenuSub: FunctionComponent<ContextMenuSubProps> = ({ children, onOpenChange, open, ...props }) => {
    return (
        <Sub open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Sub>
    );
};

export const ContextMenuSubTrigger: FunctionComponent<ContextMenuSubTriggerProps> = ({
    children,
    className,
    ...props
}) => {
    return (
        <SubTrigger
            className={clsx(
                baseItemClasses,
                'justify-between',
                '[&_svg]:text-gray-600',
                'hover:bg-gray-200',
                highlightedClasses,
                'data-[state=open]:bg-gray-200',
                'data-[disabled]:[&_svg]:text-gray-400',
                className,
            )}
            {...props}
        >
            {children}
        </SubTrigger>
    );
};

export const ContextMenuSubContent: FunctionComponent<ContextMenuSubContentProps> = ({
    alignOffset,
    children,
    className,
    sideOffset,
    ...props
}) => {
    return (
        <Portal>
            <SubContent
                alignOffset={alignOffset}
                className={clsx(contentClasses, className)}
                sideOffset={sideOffset}
                {...props}
            >
                {children}
            </SubContent>
        </Portal>
    );
};

export const ContextMenuContent: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return (
        <Portal>
            <Content className={contentClasses}>{children}</Content>
        </Portal>
    );
};

export const ContextMenuItemIndicator: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return (
        <ItemIndicator className="inline-flex items-center justify-center text-gray-900 ml-s">{children}</ItemIndicator>
    );
};

export const ContextMenuRadioGroup: FunctionComponent<ContextMenuRadioGroupProps> = ({ children, ...props }) => {
    return (
        <RadioGroup className={disabledGroupClasses} {...props}>
            {children}
        </RadioGroup>
    );
};

export const ContextMenuRadioItem: FunctionComponent<ContextMenuRadioItemProps> = ({
    children,
    className,
    ...props
}) => {
    return (
        <RadioItem
            className={clsx(baseItemClasses, 'hover:bg-gray-200', highlightedClasses, checkedClasses, className)}
            {...props}
        >
            {children}
        </RadioItem>
    );
};

export const ContextMenuSubTriggerContent: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return <div className="flex items-center gap-xs">{children}</div>;
};
